import Phaser from 'phaser';
import type { Card, GameState } from '../types';
import { COLORS } from '../main';
import { describeCost } from '../game/cost';
import { isAcquirable, isAdjacentToLastAcquired } from '../game/adjacency';
import { canAfford } from '../game/cost';
import { makeDieSprite } from './diceRender';

export interface CardLayoutPos {
  x: number;
  y: number;
  width: number;
  height: number;
}

export class CardSprite {
  scene: Phaser.Scene;
  container: Phaser.GameObjects.Container;
  bg: Phaser.GameObjects.Rectangle;
  costText: Phaser.GameObjects.Text;
  rewardText: Phaser.GameObjects.Text;
  diceLayer: Phaser.GameObjects.Container;
  card: Card;
  layout: CardLayoutPos;
  acquiredOverlay: Phaser.GameObjects.Text | null = null;

  constructor(scene: Phaser.Scene, card: Card, layout: CardLayoutPos) {
    this.scene = scene;
    this.card = card;
    this.layout = layout;
    this.container = scene.add.container(layout.x + layout.width / 2, layout.y + layout.height / 2);

    this.bg = scene.add
      .rectangle(0, 0, layout.width, layout.height, COLORS.card)
      .setStrokeStyle(2, COLORS.border);
    this.bg.setInteractive({ useHandCursor: true });

    const fontSize = Math.max(9, Math.floor(layout.width * 0.16));
    this.costText = scene.add
      .text(0, -layout.height * 0.32, describeCost(card.cost), {
        fontFamily: 'system-ui, sans-serif',
        fontSize: `${fontSize}px`,
        fontStyle: 'bold',
        color: '#e6edf3',
        align: 'center',
        wordWrap: { width: layout.width * 0.92 },
      })
      .setOrigin(0.5);

    const rewardFontSize = Math.max(7, Math.floor(layout.width * 0.12));
    this.rewardText = scene.add
      .text(0, layout.height * 0.36, this.formatRewards(), {
        fontFamily: 'system-ui, sans-serif',
        fontSize: `${rewardFontSize}px`,
        color: '#fbbf24',
        align: 'center',
        wordWrap: { width: layout.width * 0.92 },
      })
      .setOrigin(0.5);

    this.diceLayer = scene.add.container(0, 0);

    this.container.add([this.bg, this.costText, this.rewardText, this.diceLayer]);
  }

  formatRewards(): string {
    if (this.card.rewards.length === 0) return '—';
    return this.card.rewards
      .map((r) => {
        if (r.kind === 'addDie') {
          const c = r.die.color[0]!.toUpperCase();
          return r.die.faces === 6 ? `+${c}` : `+${c}d${r.die.faces}`;
        }
        return r.token === 'reroll' ? '+↻' : '+⇄';
      })
      .join(' ');
  }

  setOnTap(cb: () => void): void {
    this.bg.removeAllListeners();
    this.bg.on('pointerdown', cb);
  }

  refresh(state: GameState, hoverPlaceableDieId?: string | null): void {
    const { card, layout } = this;
    const updated = state.pyramid.find((c) => c.id === card.id);
    if (updated) this.card = updated;

    let fill = COLORS.card;
    let strokeColor = COLORS.border;
    let strokeWidth = 2;

    if (this.card.acquired) {
      fill = COLORS.cardAcquired;
    } else if (isAcquirable(this.card, state)) {
      fill = COLORS.cardAffordable;
      strokeColor = COLORS.accent;
      strokeWidth = 3;
    } else if (
      state.lastAcquired &&
      isAdjacentToLastAcquired(this.card, state.lastAcquired)
    ) {
      fill = COLORS.cardAdjacent;
    }
    this.bg.setFillStyle(fill);
    this.bg.setStrokeStyle(strokeWidth, strokeColor);

    this.diceLayer.removeAll(true);
    const placed = this.card.placedDice;
    if (placed.length > 0) {
      const dieSize = Math.min(layout.width * 0.22, layout.height * 0.18);
      const totalWidth = placed.length * dieSize + (placed.length - 1) * 4;
      const startX = -totalWidth / 2 + dieSize / 2;
      placed.forEach((d, i) => {
        const dg = makeDieSprite(
          this.scene,
          startX + i * (dieSize + 4),
          layout.height * 0.06,
          dieSize,
          d
        );
        this.diceLayer.add(dg.container);
      });
    }

    // "Need" hint when can't afford with what's placed
    if (
      !this.card.acquired &&
      placed.length > 0 &&
      !canAfford(this.card.cost, placed)
    ) {
      // No-op for v1 — visual is enough.
    }

    if (this.card.acquired && !this.acquiredOverlay) {
      this.acquiredOverlay = this.scene.add
        .text(0, -layout.height * 0.05, '✓', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: `${Math.floor(layout.height * 0.4)}px`,
          color: '#34d399',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      this.container.add(this.acquiredOverlay);
    }

    if (hoverPlaceableDieId) {
      // (reserved for drag-target highlight; v1 click-to-place doesn't need this)
    }
  }
}
